import { useState } from "react";
import PrimaryButton from "../../CommonComponents/PrimaryButton";
import FileUploadField from "../../CommonComponents/FileUploadField";
import CameraCapture from "../../CommonComponents/CameraCapture";
import { useTheme } from "../../CommonComponents/ThemeProvider";
import tinycolor from "tinycolor2";

const SubmitPreReleaseSection = ({
    photo,
    setPhoto,
    remarks,
    setRemarks,
    locationStatus,
    locationError,
    hasLocation,
    onRequestLocation,
    handleSubmit,
    handleClear,
    loading,
    error
}) => {
    const [photoMode, setPhotoMode] = useState("upload");
    const { currentTheme } = useTheme();
    const primaryColor = currentTheme?.color || "#14532d";
    const hoverColor = tinycolor(primaryColor).lighten(8).toString();
    const activeColor = tinycolor(primaryColor).darken(8).toString();

    return (
        <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden flex flex-col p-3 md:p-5 min-h-[500px]">
            <h3 className="font-semibold text-xs md:text-sm mb-2 text-brand-green tracking-tight uppercase letter-spacing-wide">3. Submit Request</h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 flex-1 text-base">
                {/* Photo upload or camera capture */}
                <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-700 flex items-center gap-1">
                        Photo
                        <span className="inline-block text-gray-400"><svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 5v14m7-7H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></span>
                    </label>
                    <div className="flex gap-2 mb-2">
                        <button type="button" onClick={() => setPhotoMode("upload")}
                            className={`px-3 py-1 rounded-lg border ${photoMode === "upload" ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-gray-300 text-gray-700"}`}>Upload</button>
                        <button type="button" onClick={() => setPhotoMode("camera")}
                            className={`px-3 py-1 rounded-lg border ${photoMode === "camera" ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-gray-300 text-gray-700"}`}>Camera</button>
                    </div>
                    {photoMode === "upload" ? (
                        <FileUploadField
                            label="Select Photo"
                            accept="image/*"
                            value={photo}
                            preview={photo ? URL.createObjectURL(photo) : null}
                            onChange={setPhoto}
                            onRemove={() => setPhoto(null)}
                            optional={true}
                        />
                    ) : (
                        <CameraCapture onCapture={blob => setPhoto(new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" }))} />
                    )}
                </div>
                {/* Location */}
                <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-700 flex items-center gap-1">
                        Location
                        <span className="text-xs font-normal text-gray-400">(Optional)</span>
                        <span className="inline-block text-gray-400"><svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" fill="currentColor"/></svg></span>
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={onRequestLocation}
                            className="px-3 py-1 rounded-lg border bg-white border-gray-300 text-gray-700"
                        >{locationStatus === "requesting" ? "Requesting..." : hasLocation ? "Location Captured" : "Enable Location"}</button>
                        <span className={`text-xs ${hasLocation ? "text-green-600" : "text-gray-500"}`}>
                            {hasLocation ? "Location ready" : "Add location if you want to include it"}
                        </span>
                    </div>
                    {locationError && (
                        <div className="text-xs text-red-600 mt-1">{locationError}</div>
                    )}
                </div>

                {/* Remarks */}
                <div>
                    <label className="block mb-1 text-sm font-semibold text-gray-700 flex items-center gap-1" htmlFor="remarks-input">
                        Remarks
                        <span className="inline-block text-gray-400"><svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M3 5h18M3 12h18M3 19h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></span>
                    </label>
                    <input
                        id="remarks-input"
                        type="text"
                        value={remarks}
                        onChange={e => setRemarks(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-brand-green"
                        maxLength={200}
                        aria-label="Remarks"
                        placeholder="Remarks (optional)"
                    />
                </div>
                <div className="flex gap-2 mt-4">
                    <PrimaryButton
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 text-base rounded-lg"
                        style={{ background: primaryColor, color: "#fff" }}
                        onMouseOver={e => e.currentTarget.style.background = hoverColor}
                        onMouseOut={e => e.currentTarget.style.background = primaryColor}
                        onMouseDown={e => e.currentTarget.style.background = activeColor}
                        onMouseUp={e => e.currentTarget.style.background = hoverColor}
                    >{loading ? "Submitting..." : "Submit"}</PrimaryButton>
                    <PrimaryButton
                        type="button"
                        onClick={handleClear}
                        disabled={loading}
                        className="px-4 py-2 text-base rounded-lg bg-gray-200 text-gray-700 border border-gray-300"
                        style={{ background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}
                    >Clear</PrimaryButton>
                </div>
                {error && (
                    <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-semibold shadow-sm animate-fade-in">
                        <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12A9 9 0 1 1 3 12a9 9 0 0 1 18 0Z" /></svg>
                        {error}
                    </div>
                )}
            </form>
        </div>
    );
};

export default SubmitPreReleaseSection;
